/**
 * Стрічка, що тримається низу: нове повідомлення — прокрутка вниз, але тільки
 * якщо людина й так була внизу (відгорнула вгору читати старе — не смикаємо).
 *
 * Головне тут — картинки. Стрічку прокручуємо одразу, як повідомлення стало в
 * DOM, а картинка в ньому доїжджає пізніше й розсуває стрічку на сотню
 * пікселів. Подія scroll при цьому не приходить (прокрутки ж не було), тож
 * чат лишався «застряглим» над останнім повідомленням — рівно на висоту
 * картинки. Тому ловимо ще й завантаження медіа (load у фазі перехоплення —
 * воно не спливає) і зміну розміру самого вікна стрічки: якщо ми були внизу,
 * лишаємось унизу.
 *
 * @param {Ref<HTMLElement|null>} listRef прокручуваний контейнер
 * @param {object} [options]
 * @param {number} [options.zone] скільки пікселів від низу ще вважається «внизу»
 */
export const useStickyScroll = (listRef, { zone = 80 } = {}) => {
  const stuck = ref(true)
  // Скільки прийшло, поки читали старе, — щоб кнопка «вниз» не була німою
  const missed = ref(0)

  const scrollToBottom = () => {
    const el = listRef.value
    if (!el) return
    el.scrollTop = el.scrollHeight
    stuck.value = true
    missed.value = 0
  }

  const onScroll = () => {
    const el = listRef.value
    if (!el) return
    stuck.value = el.scrollHeight - el.scrollTop - el.clientHeight < zone
    if (stuck.value) missed.value = 0
  }

  // Щось у стрічці виросло саме — лишаємось унизу, якщо там і були
  const keep = () => {
    if (stuck.value) scrollToBottom()
  }

  /**
   * Прийшли нові повідомлення. added — скільки саме (для лічильника
   * пропущеного)
   */
  const onAppend = (added = 0) => {
    if (stuck.value) {
      nextTick(scrollToBottom)
      return
    }
    if (added > 0) missed.value += added
  }

  let resizeObserver = null
  let bound = null

  const unbind = () => {
    if (bound) {
      bound.removeEventListener('load', keep, true)
      bound.removeEventListener('loadedmetadata', keep, true)
    }
    resizeObserver?.disconnect()
    bound = null
  }

  const bind = (el) => {
    unbind()
    if (!el || !import.meta.client) return
    bound = el
    el.addEventListener('load', keep, true)
    el.addEventListener('loadedmetadata', keep, true)

    // Змінилась висота самого вікна (над полем зʼявились вкладення, панель
    // відповіді, розгорнули колонку) — низ теж не має губитись
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(keep)
      resizeObserver.observe(el)
    }
  }

  if (import.meta.client) {
    watch(listRef, bind, { immediate: true, flush: 'post' })
    onBeforeUnmount(unbind)
  }

  return { stuck, missed, scrollToBottom, onScroll, onAppend }
}
